/**
 * @file OnboardingStatus.jsx
 * @description Main application interface with role-specific features
 * 
 * @section Responsibilities
 * - Manage role-based views
 * - Handle data aggregation
 * - Control real-time updates
 * - Manage user preferences
 * 
 * @section Dependencies
 * - Role Management: Access control
 * - Data Services: Information handling
 * - State Management: Global state
 * 
 * @section Integration Points
 * - Data API: Information retrieval
 * - Role System: Access control
 * - Notification System: Real-time updates
 * 
 * @section State Management
 * - dashboardData: Aggregated information
 * - loadingState: Data fetching
 * - errorState: Error handling
 * 
 * @section Events
 * - onDataUpdate: Data changes
 * - onError: Error handling
 * - onPreferenceChange: Settings updates
 * 
 * @section Props
 * @prop {String} userRole - Current user role
 * @prop {Object} permissions - User permissions
 */

import React from "react";
import Widget from "../../widgets/Widget";
import DgButton from "../../../../components/DgButton";
import { useNavigate } from "react-router";

function OnboardingStatus({ advisor, application = {} }) {
  const {
    id = "-",
    status = "-",
    applicationType = { type: "" },
  } = application;
  const navigate = useNavigate();
  const screenContent = {
    title: "Welcome to Ad Deum Funds",
    subtitle:
      "Thank you for starting your onboarding process with us. You're just a few steps away from unlocking all the features and benefits we offer.",
    description:
      "An advisor from our team will be in touch with you shortly to help you complete your onboarding. They will guide you through the final steps and answer any questions you may have, ensuring a smooth and personalized experience.",
    illo: "/assets/illos/hand2.png",
  };

  if (advisor.name) {
    screenContent.title = "Welcome back to Ad Deum";
    screenContent.subtitle =
      "Great news! You've been successfully assigned to one of our expert advisors, " +
      advisor.name +
      ", who is currently working diligently to complete your onboarding process. We're committed to ensuring you have the best possible start with us.";
    screenContent.description =
      "No problem! You can easily contact " +
      advisor.name +
      " for any questions or updates regarding your onboarding. Simply click the button below:";
    screenContent.illo = "/assets/illos/hand.png";
  }

  return (
    <Widget name={"onboarding-status"}>
      <div className="max-widget-height wrapper">
        <div className="heading">
          <h1 className="title">{screenContent.title}</h1>
          <p className="description">{screenContent.subtitle}</p>
        </div>
        <img src={screenContent.illo} className="illustration" />
        <strong className="subtitle">
          This is the status of your application:
        </strong>
        <p>
          Application ID: <b>{id}</b> <br />
          Status: <b>{status}</b> <br />
          Type: <b>{applicationType.type.replaceAll("_", " ")}</b> <br />
        </p>
        <br />
        {status === "REJECTED" && (
          <>
            <strong className="subtitle">Your application was rejected?</strong>
            <br />
            <DgButton
              category="secondary"
              size="medium"
              text="Create new one"
              action={() => {
                navigate("/onboarding/your-profile");
              }}
            />
          </>
        )}
        <br />
        {advisor.name !== "" && (
          <strong className="subtitle">Need to get in touch?</strong>
        )}
        <p className="description">{screenContent.description}</p>
        {advisor.phone !== "" && (
          <DgButton
            category="primary"
            size="medium"
            text="Contact Advisor"
            action={() => {
              window.open(`https://wa.me/${advisor.phone}`, "_blank");
            }}
          ></DgButton>
        )}
      </div>
    </Widget>
  );
}

export default OnboardingStatus;
